import React, { useEffect, useState, useCallback } from 'react';
import NewEntryPopup, { NewEntry } from './NewEntryPopup';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../../services/supabase';
import { useNotificationsStore } from '../../stores/notificationsStore';

interface NewEntryNotificationProviderProps {
  children: React.ReactNode;
}

// Custom event for new entries (can be triggered from anywhere in the app)
export const NEW_ENTRY_EVENT = 'new-entry-created';

export const triggerNewEntryNotification = (entry: NewEntry) => {
  window.dispatchEvent(new CustomEvent(NEW_ENTRY_EVENT, { detail: entry }));
};

const NewEntryNotificationProvider: React.FC<NewEntryNotificationProviderProps> = ({ children }) => {
  const [currentEntry, setCurrentEntry] = useState<NewEntry | null>(null);
  const [queue, setQueue] = useState<NewEntry[]>([]);

  // Get notification store actions
  const { notifyNewCandidate, addNotification } = useNotificationsStore();

  // Get current user ID to avoid showing notifications for own entries
  const getCurrentUserId = (): string | null => {
    try {
      const userStr = localStorage.getItem('user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id || null;
      }
    } catch (e) {
      console.error('Error getting current user:', e);
    }
    return null;
  };

  // Handle custom events for new entries
  const handleNewEntryEvent = useCallback((event: CustomEvent<NewEntry>) => {
    const entry = event.detail;
    addToQueue(entry);
  }, []);

  // Add entry to queue
  const addToQueue = (entry: NewEntry) => {
    setQueue(prev => [...prev, entry]);
  };

  // Process queue - show next entry when current one is closed
  useEffect(() => {
    if (!currentEntry && queue.length > 0) {
      const [next, ...rest] = queue;
      setCurrentEntry(next);
      setQueue(rest);
    }
  }, [currentEntry, queue]);

  // Custom-Events (triggerNewEntryNotification) funktionieren unabhängig von Realtime.
  useEffect(() => {
    window.addEventListener(NEW_ENTRY_EVENT, handleNewEntryEvent as EventListener);
    return () => {
      window.removeEventListener(NEW_ENTRY_EVENT, handleNewEntryEvent as EventListener);
    };
  }, [handleNewEntryEvent]);

  // Supabase-Realtime nur mit echter Supabase-Auth-Session.
  // Die App authentifiziert über Backend-Cookies, nicht über Supabase Auth —
  // ohne Session würde der Websocket mit dem anon-Key dauerhaft 401 bekommen
  // und endlos neu verbinden (und dürfte per RLS ohnehin keine Events sehen).
  useEffect(() => {
    let cancelled = false;
    let candidatesSubscription: RealtimeChannel | null = null;
    let companiesSubscription: RealtimeChannel | null = null;

    const setup = async () => {
      let session = null;
      try {
        ({ data: { session } } = await supabase.auth.getSession());
      } catch {
        session = null;
      }
      if (cancelled || !session?.access_token) return;

      const currentUserId = getCurrentUserId();

      // Subscribe to new candidates
      candidatesSubscription = supabase
        .channel('new-candidates')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'candidates'
          },
          (payload) => {
            const candidate = payload.new as any;
            // Don't show notification if created by current user
            if (candidate.created_by === currentUserId) return;

            const entry: NewEntry = {
              type: 'candidate',
              id: candidate.id,
              first_name: candidate.first_name || '',
              last_name: candidate.last_name || '',
              current_position: candidate.current_position,
              city: candidate.city,
              created_by_name: candidate.created_by_name
            };
            addToQueue(entry);

            // Add notification to store
            const candidateName = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'Unbekannt';
            const position = candidate.current_position || 'Kandidat';
            notifyNewCandidate(candidateName, position, 0); // Match score can be calculated later
          }
        )
        .subscribe();

      // Subscribe to new companies
      companiesSubscription = supabase
        .channel('new-companies')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'companies'
          },
          (payload) => {
            const company = payload.new as any;
            // Don't show notification if created by current user
            if (company.created_by === currentUserId) return;

            const entry: NewEntry = {
              type: 'company',
              id: company.id,
              name: company.name || '',
              industry: company.industry,
              billing_city: company.billing_city,
              created_by_name: company.created_by_name
            };
            addToQueue(entry);

            // Add notification to store
            const companyName = company.name || 'Unbekanntes Unternehmen';
            const location = company.billing_city || company.city || '';
            addNotification({
              type: 'alert',
              title: 'Neues Unternehmen erstellt',
              description: `${companyName}${location ? ` in ${location}` : ''}`,
              time: 'gerade eben',
              unread: true,
              priority: 'normal'
            });
          }
        )
        .subscribe();
    };

    setup();

    return () => {
      cancelled = true;
      if (candidatesSubscription) supabase.removeChannel(candidatesSubscription);
      if (companiesSubscription) supabase.removeChannel(companiesSubscription);
    };
  }, [addNotification, notifyNewCandidate]);

  const handleClose = () => {
    setCurrentEntry(null);
  };

  return (
    <>
      {children}
      <NewEntryPopup entry={currentEntry} onClose={handleClose} />
    </>
  );
};

export default NewEntryNotificationProvider;
